/**
 * Fiskal Cloud Archive API
 * # Overview  ## Cash point closing data Archive API is used for storing cash point closing data in *DFKA* and *DSFinV-k* formats. These formats are stored along with binary data (receipts, references, ...) also known as `attachments` and makes together bundle called `import`. Imports are stored in archive for 10+ years. User is able to query the imports.  Currently supported *DFKA* versions are: * 2.1.0 * 2.1.1 * 2.2.0 * 2.3.0  Currently supported *DSFinV-K* versions are: * 2.1 * 2.2 * 2.3 * 2.4  ## TSS logs data If archiving is enabled for given business premises and *encryption key* is provided in Fiskal Cloud, TSS logs are being processed automatically from *short therm archive* to archive. However, if encryption key is not provided we should upload TSS logs via archive API. These logs can\'t be encrypted.  TSS log is `zip` archive which: * must contain `tar` archive named `export.tar` which contains signatures * could contain `tar` archive named `audit.tar` which contains additional signatures (mainly in case of HW token time reset)  # Generic workflow  ## API Access Import API uses [OAuth2 Access Token](https://www.oauth.com/oauth2-servers/access-tokens/access-token-response/) with password flow and two realms: * `df` realm used for users authentication with user\'s email and password. Client used for authorization is `df-api`. * `df-archive-m2m` realm used for technical users and API access mainly for purposes of uploading data to archive. Client used for authorization is `df-archive-api`. To be able to upload some data to archive, you should create API Access user in management portal. After that you can obtain authentication token like on following example:  ``` curl -X POST \\ https://fiskal.cloud/auth/realms/df-archive-m2m/protocol/openid-connect/token \\ -H \'Accept: application/json\' \\ -H \'Content-Type: application/x-www-form-urlencoded\' \\ -d \'username=<API_USER>&password=<API_USER_PASSWORD>&client_id=df-archive-api&grant_type=password\' ``` Note: `API_USER` is generated name in form of `archiving-abcd-efgh` where `abcd-efgh` are randomly generated characters and numbers.  Please use `access_token` from response in subsequent calls as `Authorization` header with `Bearer` token type. `Authorization: Bearer {{Access token from authentication token response}}`  ### Tenant context All methods of this API work in the context of the logged in user\'s tenant account. For creating new elements you need to set the tenant id in the request. You can retrieve your tenant id by using the /tenants/tenant endpoint. It is fixed, so it can safely stored for later use.  ### Managed Tenant You can use a different tenant for operations if you have a permission to managed them. You can select managed tenant by sending `X-Managed-Tenant` header with `tenantId` of the managed tenant. All operations (creating, updating and getting) are done in the context of the managed tenant with this header.  ``` curl -X GET \\ -i \'https://fiskal.cloud/archiving-api/business-premises/{businessPremisesId}/imports\' \\ -H \'Authorization: Bearer {{AccessToken}}\' \\ -H \'X-Managed-Tenant: {{managedTenantId}}\' ```   ## Import of cash point closing data Given that the sender has been assigned to a business premises of some kind, it knows its ID. Therefore, presumably during the end of the day report, we want to upload its transactions into the archive.  First, we need to create a new _import_, which is a \"workspace\" into which the report and its attachments are uploaded. To do it, we need to send a multipart/form-data request. The `metadata` field contains JSON with metadata, the `file` field contains the source file itself. It\'s also needed to specify `filename` and `type` (which could be one of `application/json`, `application/zip`, `application/gzip`) fields with `file` field.  ``` curl \\ --request POST \\ -H \'Authorization: Bearer {{AccessToken}}\' \\ -H \'accept: application/json\' \\ -H \'Content-Type: multipart/form-data\' \\ --form \'metadata={\"type\":\"DSFINVK\",\"version\":\"2.3\"};type=application/json\' \\ --form \'file=@dsfinvk.zip;filename=dsfinvk.zip;type=application/zip\' \\ \'https://fiskal.cloud/archiving-api/business-premises/34876479-83fe-49e7-b03c-2ddc881d89d4/imports\' ```  The result of this call is a UUID `id`, which then serves as the ID of the import \"workspace\". Also, the `Location` header is set appropriately.  ``` {   \"id\": 3fa85f64-5717-4562-b3fc-2c963f66afa6\"   ... } ```  Once we\'ve got `importId`, we can start uploading attachments. Again as multipart/form-data requests together with metadata. We can use: * bulk upload (preffered before single upload) where we upload multiple attachments at once in one archive file   ```   curl \\   --request POST \\   -H \'Authorization: Bearer {{AccessToken}}\' \\   -H \'Content-Type: multipart/form-data\' \\   --form \'file=@attachments_part1.zip;filename=attachments_part1.zip;type=application/zip\' \\   https://fiskal.cloud/archiving-api/imports/3fa85f64-5717-4562-b3fc-2c963f66afa6/attachments/bulk   ```  * single upload where we upload one attachment at time   ```   curl \\   --request POST \\   -H \'Authorization: Bearer {{AccessToken}}\' \\   -H \'Content-Type: multipart/form-data\' \\   -H \'accept: application/json\' \\   --form \'metadata={\"referenceId\": xyz\"};type=application/json\'    --form \'file=@attachment-xyz.pdf;filename=attachment-xyz.pdf;type=application/pdf\' \\   https://fiskal.cloud/archiving-api/imports/3fa85f64-5717-4562-b3fc-2c963f66afa6/attachments   ```  Both operations could be executed multiple times, just the content of file can\'t be the same. Once we\'re ok with everything, we can mark the import as finished and by locking it - that renders the import final and immutable:  ``` curl \\ --request POST \\ -H \'Authorization: Bearer {{AccessToken}}\' \\ -H \'Content-Length: 0\' \\ https://fiskal.cloud/archiving-api/imports/3fa85f64-5717-4562-b3fc-2c963f66afa6/lock ```  ### Validation  While importing data system performs validations described below. The maximum size of a DSFinV-K or DFKA file is 35 MB.  #### DFKA validation  Currently data are validation only against json schema.  #### DSFINVK validation  * all files must be encoded in UTF-8 * following csv files must be present even empty, but the csv header must be included   allocation_groups.csv, businesscases.csv, cash_per_currency.csv, cashpointclosing.csv,   cashregister.csv, datapayment.csv, itemamounts.csv, lines.csv, lines_vat.csv,   location.csv, pa.csv, payment.csv, references.csv, slaves.csv, subitems.csv,   transactions.csv, transactions_tse.csv, transactions_vat.csv, tse.csv,   vat.csv * file cashpointclosing.csv must not be empty * file cashpointclosing.csv have following required (ie. must not be empty) fields:   Z_SE_BARZAHLUNGEN, Z_SE_ZAHLUNGEN, LAND, ORT, PLZ, TAXONOMIE_VERSION, Z_NR, Z_ERSTELLUNG, Z_KASSE_ID * file cashregister.csv must not be emtpy * cash register that is referenced inside cashpointclosing.csv must exists in cashregisters.csv * DTD schema file (gdpdu-01-08-2002.dtd or gdpdu-01-09-2004.dtd) must be included * index.xml file must be included and it must be valid against DTD schema  ## Import of TSS logs  When we want to use archive api to upload TSS logs to archive we must send `multipart/form-data` request along with `metadata` field. ``` curl \\ --request POST \\ -H \'Authorization: Bearer {{AccessToken}}\' \\ -H \'Content-Type: multipart/form-data\' \\ --form \'metadata={\"tssId\": tss\"}\'  --form \'file=@tsslog.tar.gz\' \\ https://fiskal.cloud/archiving-api/business-premises/34876479-83fe-49e7-b03c-2ddc881d89d4/tss-logs ```  This will process input file and create tss log(s) for given business day(s).  # Changelog ## 0.38.0 * DSFinV-K and DFKA size limit added. * Asynchronous antivirus scan for procedural documentation and open archive.  ## 0.37.0 * Remove deprecated webhook  ## 0.36.0 * Support DSFinV-K 2.4 
 *
 * The version of the OpenAPI document: 0.38.0
 * Contact: info@deutsche-fiskal.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */
/* tslint:disable:no-unused-variable member-ordering */

import { Inject, Injectable, Optional }                      from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams,
         HttpResponse, HttpEvent, HttpParameterCodec, HttpContext 
        }       from '@angular/common/http';
import { CustomHttpParameterCodec }                          from '../encoder';
import { Observable }                                        from 'rxjs';

// @ts-ignore
import { CreateKeywordRequestDTO } from '../model/create-keyword-request-dto';
// @ts-ignore
import { KeywordDTO } from '../model/keyword-dto';
// @ts-ignore
import { ListKeywordsResponseDTO } from '../model/list-keywords-response-dto';
// @ts-ignore
import { UpdateKeywordRequestDTO } from '../model/update-keyword-request-dto';

// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS }                     from '../variables';
import { Configuration }                                     from '../configuration';
import {
    KeywordsApiClientInterface,
    CreateKeywordRequestParams,
    DeleteKeywordRequestParams,
    GetKeywordRequestParams,
    GetKeywordsRequestParams,
    UpdateKeywordRequestParams
} from './keywords-api-client.serviceInterface';



@Injectable({
  providedIn: 'root'
})
export class KeywordsApiClient implements KeywordsApiClientInterface {

    protected basePath = 'https://fiskal.cloud/archiving-api';
    public defaultHeaders = new HttpHeaders();
    public configuration = new Configuration();
    public encoder: HttpParameterCodec;

    constructor(protected httpClient: HttpClient, @Optional()@Inject(BASE_PATH) basePath: string|string[], @Optional() configuration: Configuration) {
        if (configuration) {
            this.configuration = configuration;
        }
        if (typeof this.configuration.basePath !== 'string') {
            if (Array.isArray(basePath) && basePath.length > 0) {
                basePath = basePath[0];
            }

            if (typeof basePath !== 'string') {
                basePath = this.basePath;
            }
            this.configuration.basePath = basePath;
        }
        this.encoder = this.configuration.encoder || new CustomHttpParameterCodec();
    }


    // @ts-ignore
    private addToHttpParams(httpParams: HttpParams, value: any, key?: string): HttpParams {
        if (typeof value === "object" && value instanceof Date === false) {
            httpParams = this.addToHttpParamsRecursive(httpParams, value);
        } else {
            httpParams = this.addToHttpParamsRecursive(httpParams, value, key);
        }
        return httpParams;
    }

    private addToHttpParamsRecursive(httpParams: HttpParams, value?: any, key?: string): HttpParams {
        if (value == null) {
            return httpParams;
        }

        if (typeof value === "object") {
            if (Array.isArray(value)) {
                (value as any[]).forEach( elem => httpParams = this.addToHttpParamsRecursive(httpParams, elem, key));
            } else if (value instanceof Date) {
                if (key != null) {
                    httpParams = httpParams.append(key, (value as Date).toISOString().substr(0, 10));
                } else {
                   throw Error("key may not be null if value is Date");
                }
            } else {
                Object.keys(value).forEach( k => httpParams = this.addToHttpParamsRecursive(
                    httpParams, value[k], key != null ? `${key}.${k}` : k));
            }
        } else if (key != null) {
            httpParams = httpParams.append(key, value);
        } else {
            throw Error("key may not be null if value is not object or array");
        }
        return httpParams;
    }

    /**
     * Create keyword
     * Create new keyword
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public createKeyword(requestParameters: CreateKeywordRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<KeywordDTO>;
    public createKeyword(requestParameters: CreateKeywordRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<KeywordDTO>>;
    public createKeyword(requestParameters: CreateKeywordRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<KeywordDTO>>;
    public createKeyword(requestParameters: CreateKeywordRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<any> {
        const createKeywordRequestDTO = requestParameters.createKeywordRequestDTO;

        let localVarHeaders = this.defaultHeaders;

        let localVarCredential: string | undefined;
        // authentication (management_auth) required
        localVarCredential = this.configuration.lookupCredential('management_auth');
        if (localVarCredential) {
            localVarHeaders = localVarHeaders.set('Authorization', 'Bearer ' + localVarCredential);
        }

        let localVarHttpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }

        let localVarHttpContext: HttpContext | undefined = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }


        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
        }

        let responseType_: 'text' | 'json' | 'blob' = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            } else {
                responseType_ = 'blob';
            }
        }

        let localVarPath = `/keywords`;
        return this.httpClient.request<KeywordDTO>('post', `${this.configuration.basePath}${localVarPath}`,
            {
                context: localVarHttpContext,
                body: createKeywordRequestDTO,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: localVarHeaders,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Delete keyword
     * Delete existing keyword
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public deleteKeyword(requestParameters: DeleteKeywordRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<any>;
    public deleteKeyword(requestParameters: DeleteKeywordRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<HttpResponse<any>>;
    public deleteKeyword(requestParameters: DeleteKeywordRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<HttpEvent<any>>;
    public deleteKeyword(requestParameters: DeleteKeywordRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: undefined, context?: HttpContext}): Observable<any> {
        const keywordId = requestParameters.keywordId;
        if (keywordId === null || keywordId === undefined) {
            throw new Error('Required parameter keywordId was null or undefined when calling deleteKeyword.');
        }

        let localVarHeaders = this.defaultHeaders;

        let localVarCredential: string | undefined;
        // authentication (management_auth) required
        localVarCredential = this.configuration.lookupCredential('management_auth');
        if (localVarCredential) {
            localVarHeaders = localVarHeaders.set('Authorization', 'Bearer ' + localVarCredential);
        }

        let localVarHttpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }

        let localVarHttpContext: HttpContext | undefined = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }


        let responseType_: 'text' | 'json' | 'blob' = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            } else {
                responseType_ = 'blob';
            }
        }

        let localVarPath = `/keywords/${this.configuration.encodeParam({name: "keywordId", value: keywordId, in: "path", style: "simple", explode: false, dataType: "string", dataFormat: "uuid"})}`;
        return this.httpClient.request<any>('delete', `${this.configuration.basePath}${localVarPath}`,
            {
                context: localVarHttpContext,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: localVarHeaders,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Get keyword
     * Get existing keyword
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public getKeyword(requestParameters: GetKeywordRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<KeywordDTO>;
    public getKeyword(requestParameters: GetKeywordRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<KeywordDTO>>;
    public getKeyword(requestParameters: GetKeywordRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<KeywordDTO>>;
    public getKeyword(requestParameters: GetKeywordRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<any> {
        const keywordId = requestParameters.keywordId;
        if (keywordId === null || keywordId === undefined) {
            throw new Error('Required parameter keywordId was null or undefined when calling getKeyword.');
        }

        let localVarHeaders = this.defaultHeaders;

        let localVarCredential: string | undefined;
        // authentication (management_auth) required
        localVarCredential = this.configuration.lookupCredential('management_auth');
        if (localVarCredential) {
            localVarHeaders = localVarHeaders.set('Authorization', 'Bearer ' + localVarCredential);
        }

        let localVarHttpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }

        let localVarHttpContext: HttpContext | undefined = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }


        let responseType_: 'text' | 'json' | 'blob' = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            } else {
                responseType_ = 'blob';
            }
        }

        let localVarPath = `/keywords/${this.configuration.encodeParam({name: "keywordId", value: keywordId, in: "path", style: "simple", explode: false, dataType: "string", dataFormat: "uuid"})}`;
        return this.httpClient.request<KeywordDTO>('get', `${this.configuration.basePath}${localVarPath}`,
            {
                context: localVarHttpContext,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: localVarHeaders,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Get keywords list
     * Get keywords list
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public getKeywords(requestParameters: GetKeywordsRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<ListKeywordsResponseDTO>;
    public getKeywords(requestParameters: GetKeywordsRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<ListKeywordsResponseDTO>>;
    public getKeywords(requestParameters: GetKeywordsRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<ListKeywordsResponseDTO>>;
    public getKeywords(requestParameters: GetKeywordsRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<any> {
        const name = requestParameters.name;
        const keywordIds = requestParameters.keywordIds;
        const pageNumber = requestParameters.pageNumber;
        const pageSize = requestParameters.pageSize;
        const sort = requestParameters.sort;

        let localVarQueryParameters = new HttpParams({encoder: this.encoder});
        if (name !== undefined && name !== null) {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
            <any>name, 'name');
        }
        if (keywordIds) {
            keywordIds.forEach((element) => {
                localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
                  <any>element, 'keywordIds');
            })
        }
        if (pageNumber !== undefined && pageNumber !== null) {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
            <any>pageNumber, 'pageNumber');
        }
        if (pageSize !== undefined && pageSize !== null) {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
            <any>pageSize, 'pageSize');
        }
        if (sort !== undefined && sort !== null) {
          localVarQueryParameters = this.addToHttpParams(localVarQueryParameters,
            <any>sort, 'sort');
        }

        let localVarHeaders = this.defaultHeaders;

        let localVarCredential: string | undefined;
        // authentication (management_auth) required
        localVarCredential = this.configuration.lookupCredential('management_auth');
        if (localVarCredential) {
            localVarHeaders = localVarHeaders.set('Authorization', 'Bearer ' + localVarCredential);
        }

        let localVarHttpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }

        let localVarHttpContext: HttpContext | undefined = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }


        let responseType_: 'text' | 'json' | 'blob' = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            } else {
                responseType_ = 'blob';
            }
        }

        let localVarPath = `/keywords`;
        return this.httpClient.request<ListKeywordsResponseDTO>('get', `${this.configuration.basePath}${localVarPath}`,
            {
                context: localVarHttpContext,
                params: localVarQueryParameters,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: localVarHeaders,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

    /**
     * Update keyword
     * Update existing keyword
     * @param requestParameters
     * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
     * @param reportProgress flag to report request and response progress.
     */
    public updateKeyword(requestParameters: UpdateKeywordRequestParams, observe?: 'body', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<KeywordDTO>;
    public updateKeyword(requestParameters: UpdateKeywordRequestParams, observe?: 'response', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpResponse<KeywordDTO>>;
    public updateKeyword(requestParameters: UpdateKeywordRequestParams, observe?: 'events', reportProgress?: boolean, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<HttpEvent<KeywordDTO>>;
    public updateKeyword(requestParameters: UpdateKeywordRequestParams, observe: any = 'body', reportProgress: boolean = false, options?: {httpHeaderAccept?: 'application/json', context?: HttpContext}): Observable<any> {
        const keywordId = requestParameters.keywordId;
        if (keywordId === null || keywordId === undefined) {
            throw new Error('Required parameter keywordId was null or undefined when calling updateKeyword.');
        }
        const updateKeywordRequestDTO = requestParameters.updateKeywordRequestDTO;

        let localVarHeaders = this.defaultHeaders;

        let localVarCredential: string | undefined;
        // authentication (management_auth) required
        localVarCredential = this.configuration.lookupCredential('management_auth');
        if (localVarCredential) {
            localVarHeaders = localVarHeaders.set('Authorization', 'Bearer ' + localVarCredential);
        }

        let localVarHttpHeaderAcceptSelected: string | undefined = options && options.httpHeaderAccept;
        if (localVarHttpHeaderAcceptSelected === undefined) {
            // to determine the Accept header
            const httpHeaderAccepts: string[] = [
                'application/json'
            ];
            localVarHttpHeaderAcceptSelected = this.configuration.selectHeaderAccept(httpHeaderAccepts);
        }
        if (localVarHttpHeaderAcceptSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Accept', localVarHttpHeaderAcceptSelected);
        }

        let localVarHttpContext: HttpContext | undefined = options && options.context;
        if (localVarHttpContext === undefined) {
            localVarHttpContext = new HttpContext();
        }


        // to determine the Content-Type header
        const consumes: string[] = [
            'application/json'
        ];
        const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
        if (httpContentTypeSelected !== undefined) {
            localVarHeaders = localVarHeaders.set('Content-Type', httpContentTypeSelected);
        }

        let responseType_: 'text' | 'json' | 'blob' = 'json';
        if (localVarHttpHeaderAcceptSelected) {
            if (localVarHttpHeaderAcceptSelected.startsWith('text')) {
                responseType_ = 'text';
            } else if (this.configuration.isJsonMime(localVarHttpHeaderAcceptSelected)) {
                responseType_ = 'json';
            } else {
                responseType_ = 'blob';
            }
        }

        let localVarPath = `/keywords/${this.configuration.encodeParam({name: "keywordId", value: keywordId, in: "path", style: "simple", explode: false, dataType: "string", dataFormat: "uuid"})}`;
        return this.httpClient.request<KeywordDTO>('put', `${this.configuration.basePath}${localVarPath}`,
            {
                context: localVarHttpContext,
                body: updateKeywordRequestDTO,
                responseType: <any>responseType_,
                withCredentials: this.configuration.withCredentials,
                headers: localVarHeaders,
                observe: observe,
                reportProgress: reportProgress
            }
        );
    }

}
